import { Clapperboard, LayoutDashboard } from 'lucide-react';

import type { SidebarNavItem } from '@/components/layout/Sidebar';
import type { Profile } from '@/lib/queries/profile';

// Role-aware shell chrome (Spec 27). The shells were role-blind — `profile` at
// useAuth() carries role / creator_status, and the chrome read none of it. These
// pure helpers are the single source of truth, consumed by all three shells so
// the cross-surface nav, the footer badge, and the JoinCta suppression can't
// drift apart.
//
// Founder and membership are NOT on the profile row (they live on the
// subscription); the caller reads them cheaply (the subscription/freemium
// queries are already cached on the reader shell) and passes them in, so these
// helpers stay pure and unit-testable.
//
// NOTE: nav visibility is UX, not a security boundary. Hiding the Admin link
// from a non-admin is convenience; useRequireRole on the routes is the actual
// guard. This module never conflates the two.

export type Shell = 'subscriber' | 'creator' | 'admin';

/**
 * The lateral tool-to-tool links this person can reach and isn't already in
 * (Spec 28 A6). Each entry is omitted when it equals the current shell (no
 * "Admin" link while in /admin). The return-to-home path is NOT here — it's the
 * dedicated "Home" exit affordance the tool shells render (A4). So from the
 * reader dashboard these are the tools to enter; from one tool they're the
 * sideways link to the other (a founder who is both staff and contributor).
 */
export function crossSurfaceLinks(profile: Profile | null, currentShell: Shell): SidebarNavItem[] {
  if (!profile) return [];
  const items: SidebarNavItem[] = [];

  // Admin + moderator both reach /admin (Spec 21 scoped the moderator view;
  // the link is the same, the destination self-scopes).
  const staff = profile.role === 'admin' || profile.role === 'moderator';
  if (staff && currentShell !== 'admin') {
    items.push({ to: '/admin', label: 'Admin', icon: LayoutDashboard });
  }
  if (profile.creator_status === 'approved' && currentShell !== 'creator') {
    items.push({ to: '/creator', label: 'Contributor desk', icon: Clapperboard });
  }
  return items;
}

export type RoleLabel = 'Founder' | 'Admin' | 'Moderator' | 'Contributor' | 'Member';

/**
 * The single human role word for the footer badge, in precedence order. Pure —
 * founder and membership come from the subscription (not the profile), so the
 * caller passes them in (reading the already-cached subscription/freemium
 * queries) rather than this helper doing IO. A free reader → null (no badge).
 */
export function roleLabel(
  profile: Profile | null,
  opts: { isFounder?: boolean; isMember?: boolean } = {},
): RoleLabel | null {
  if (!profile) return null;
  if (opts.isFounder) return 'Founder';
  if (profile.role === 'admin') return 'Admin';
  if (profile.role === 'moderator') return 'Moderator';
  if (profile.creator_status === 'approved') return 'Contributor';
  if (opts.isMember) return 'Member';
  return null;
}

/**
 * Whether to show the "Become a member" CTA. Hidden for anyone for whom it's a
 * category error — staff, approved contributors, and active members. Anon
 * (profile null) and free signed-in readers → shown.
 */
export function showJoinCta(profile: Profile | null, isMember: boolean): boolean {
  if (isMember) return false;
  if (!profile) return true;
  if (profile.role === 'admin' || profile.role === 'moderator') return false;
  if (profile.creator_status === 'approved') return false;
  return true;
}
